import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter } from "react-router-dom";

// Core layout components (keep eager - small and always needed)
import Faqs from "../components/dashboard/faqs";
import PageLoader from "../components/PageLoader";
import Auth from "../Layout/Auth";
import DashboardLayout from "../Layout/Dashboard";
import MessagesPage from "../pages/Dashboard/messages";
import Notifications from "../pages/Dashboard/notifications";
import SendAMessage from "../pages/Dashboard/send-a-message";
import WeddnProSubscription from "../pages/Dashboard/subscription";
import Support from "../pages/Dashboard/support";
import ErrorPage from "../pages/ErrorPage/Error";
import AuthRouteProvider from "./AuthRoutesProvider";
import { Routes } from "./baseRoutes";
import EventPlannerRouteProvider from "./EventPlannerRoutesProvider";

// Lazy load all page components for code splitting
const SignIn = lazy(() => import("../pages/Auth/SignIn"));
const RegisterForm = lazy(() => import("../pages/Auth"));
const RegisterSpouse = lazy(() => import("../pages/Auth/RegisterSpouse"));
const ResetPassword = lazy(() =>
  import("../pages/Auth/ResetPassword").then((module) => ({
    default: module.ResetPassword,
  }))
);

// Dashboard
const Overview = lazy(() => import("../pages/Dashboard"));
const CoupleWeddingPage = lazy(
  () => import("../pages/Dashboard/CoupleWeddnPage")
);
const WeddnList = lazy(
  () => import("../pages/Dashboard/EventPlanner/WeddnList")
);

// Guests
const Guests = lazy(() => import("../pages/Guests"));
const ManageGuests = lazy(() => import("../pages/Guests/ManageGuests"));
const DeletedGuests = lazy(() => import("../pages/Guests/DeletedGuests"));
const TablePlan = lazy(() => import("../pages/Guests/TablePlan"));
const WellWishes = lazy(() => import("../pages/Guests/WellWishes"));
const Messaging = lazy(() => import("../pages/Guests/Messaging"));
const MessagingPage = lazy(() => import("../pages/Guests/Messaging/messaging"));
const MessagingDetails = lazy(
  () => import("../pages/Guests/Messaging/messaging-details")
);
const MessagingTemplates = lazy(
  () => import("../pages/Guests/Messaging/templates")
);
const CreateMessage = lazy(
  () => import("../pages/Guests/Messaging/create-message")
);
const SendMessagePage = lazy(
  () => import("../pages/Guests/Messaging/send-message")
);
const AccessCards = lazy(() => import("../pages/Guests/AccessCard"));
const Preferences = lazy(() => import("../pages/Guests/Preferences"));

// Website
const Website = lazy(() => import("../pages/Website"));
const WebsiteDesign = lazy(
  () => import("../pages/Website/pages/design.website")
);
const EditWebiteLayout = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EditWebiteLayout,
  }))
);
const EditWebsite = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EditWebsite,
  }))
);
const Basics = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Basics,
  }))
);
const LoveStory = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.LoveStory,
  }))
);
const AsoEbi = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.AsoEbi,
  }))
);
const EventSchedule = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.EventSchedule,
  }))
);
const Gallery = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Gallery,
  }))
);
const Gifting = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.Gifting,
  }))
);
const QAndA = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.QAndA,
  }))
);
const WeddingParty = lazy(() =>
  import("../pages/Website/pages/EditWebsite").then((module) => ({
    default: module.WeddingParty,
  }))
);

// Settings
const Settings = lazy(() => import("../pages/Settings"));
const Account = lazy(() => import("../pages/Settings/Account"));
const Collaboration = lazy(() => import("../pages/Settings/Collaboration"));
const ShareFeedback = lazy(() => import("../pages/Settings/ShareFeedback"));
const Subscription = lazy(() => import("../pages/Settings/Subscription"));

// Catering
const CateringCmp = lazy(() => import("../pages/Catering"));
const Catering = lazy(() => import("../pages/Catering/Catering"));
const CateringCentre = lazy(() => import("../pages/Catering/CateringCentre"));

// Suspense wrapper for lazy components
const withSuspense = (Component: ComponentType) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

const router = createBrowserRouter([
  {
    path: Routes.login,
    element: <Auth>{withSuspense(SignIn)}</Auth>,
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.register,
    element: (
      <Auth>
        <RegisterForm />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.registerSpouse,
    element: (
      <Auth>
        <RegisterSpouse />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.resetPassword,
    element: (
      <Auth>
        <ResetPassword />
      </Auth>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: Routes.index,
    element: (
      <AuthRouteProvider>
        <DashboardLayout />
      </AuthRouteProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: withSuspense(Overview),
      },
      {
        path: Routes.eventPlannerWedding,
        element: <EventPlannerRouteProvider />,
        children: [
          {
            index: true,
            element: <CoupleWeddingPage />,
          },
          {
            path: Routes.eventPlannerCatering,
            element: <CateringCmp />,
            children: [
              {
                index: true,
                element: <Catering />,
              },
              {
                path: Routes.eventPlannerCateringCentre,
                element: <CateringCentre />,
              },
            ],
          },
          {
            path: Routes.eventPlannerGuests,
            element: <Guests />,
            children: [
              {
                path: Routes.eventPlannerGuestsAccessCards,
                element: <AccessCards />,
              },
              {
                path: Routes.eventPlannerGuestsWellWishes,
                element: <WellWishes />,
              },
              {
                path: Routes.eventPlannerGuestsTablePlan,
                element: <TablePlan />,
              },
              {
                path: Routes.eventPlannerGuestsMessaging,
                element: <Messaging />,
              },
              {
                path: Routes.eventPlannerGuestsPreference,
                element: <Preferences />,
              },
              {
                path: Routes.eventPlannerGuestsDeleted,
                element: <DeletedGuests />,
              },
              {
                path: Routes.eventPlannerGuests,
                element: <ManageGuests />,
              },
            ],
          },
          {
            path: Routes.eventPlannerWebsite,
            element: <Website />,
            children: [
              {
                path: Routes.eventPlannerWebsiteDesign,
                element: <WebsiteDesign />,
              },
              {
                path: Routes.eventPlannerEditWebsite,
                element: <EditWebiteLayout />,
                children: [
                  {
                    path: Routes.eventPlannerBasicsEdit,
                    element: <Basics />,
                  },
                  {
                    path: Routes.eventPlannerLoveStoryEdit,
                    element: <LoveStory />,
                  },
                  {
                    path: Routes.eventPlannerAsoEbiEdit,
                    element: <AsoEbi />,
                  },
                  {
                    path: Routes.eventPlannerEventSchedule,
                    element: <EventSchedule />,
                  },
                  {
                    path: Routes.eventPlannerGalleryEdit,
                    element: <Gallery />,
                  },
                  {
                    path: Routes.eventPlannerGiftingEdit,
                    element: <Gifting />,
                  },
                  {
                    path: Routes.eventPlannerQAndAEdit,
                    element: <QAndA />,
                  },
                  {
                    path: Routes.eventPlannerWeddingPartyEdit,
                    element: <WeddingParty />,
                  },
                  {
                    index: true,
                    element: <EditWebsite />,
                  },
                ],
              },
            ],
          },
          {
            path: Routes.eventPlannerWeddnProSubscription,
            element: <WeddnProSubscription />,
          },
          {
            path: Routes.eventSupport,
            element: <Support />,
          },
          {
            path: Routes.eventPlannerSettings,
            element: <Settings />,
            children: [
              {
                path: Routes.eventPlannerSettings,
                element: <Account />,
              },
              {
                path: Routes.eventPlannerCollaborationsSettings,
                element: <Collaboration />,
              },
              {
                path: Routes.eventPlannerShareFeedbackSettings,
                element: <ShareFeedback />,
              },
              {
                path: Routes.eventPlannerSubscriptionSettings,
                element: <Subscription />,
              },
            ],
          },
        ],
      },
      {
        path: Routes.eventPlannerWeddingList,
        element: <WeddnList />,
      },
      {
        path: Routes.guests,
        element: <Guests />,
        children: [
          {
            path: Routes.guestsAccessCards,
            element: <AccessCards />,
          },
          {
            path: Routes.guestsWellWishes,
            element: <WellWishes />,
          },
          {
            path: Routes.guestsTablePlan,
            element: <TablePlan />,
          },
          {
            path: Routes.guestsMessaging,
            element: <Messaging />,
          },
          {
            path: Routes.guestsPreference,
            element: <Preferences />,
          },
          {
            path: Routes.guestsDeleted,
            element: <DeletedGuests />,
          },
          {
            path: Routes.guests,
            element: <ManageGuests />,
          },
        ],
      },
      {
        path: Routes.catering,
        element: <CateringCmp />,
        children: [
          {
            index: true,
            element: <Catering />,
          },
          {
            path: Routes.cateringCentre,
            element: <CateringCentre />,
          },
        ],
      },
      {
        path: Routes.website,
        element: <Website />,
        children: [
          {
            path: Routes.websiteDesign,
            element: <WebsiteDesign />,
          },
          {
            path: Routes.editWebsite,
            element: <EditWebiteLayout />,
            children: [
              {
                path: Routes.basicsEdit,
                element: <Basics />,
              },
              {
                path: Routes.loveStoryEdit,
                element: <LoveStory />,
              },
              {
                path: Routes.asoEbiEdit,
                element: <AsoEbi />,
              },
              {
                path: Routes.eventSchedule,
                element: <EventSchedule />,
              },
              {
                path: Routes.galleryEdit,
                element: <Gallery />,
              },
              {
                path: Routes.giftingEdit,
                element: <Gifting />,
              },
              {
                path: Routes.qAndAEdit,
                element: <QAndA />,
              },
              {
                path: Routes.weddingPartyEdit,
                element: <WeddingParty />,
              },
              {
                index: true,
                element: <EditWebsite />,
              },
            ],
          },
        ],
      },
      {
        path: Routes.settings,
        element: <Settings />,
        children: [
          {
            path: Routes.settings,
            element: <Account />,
          },
          {
            path: Routes.CollaborationsSettings,
            element: <Collaboration />,
          },
          {
            path: Routes.ShareFeedbackSettings,
            element: <ShareFeedback />,
          },
          {
            path: Routes.SubscriptionSettings,
            element: <Subscription />,
          },
        ],
      },
      {
        path: Routes.messaging,
        element: withSuspense(MessagingPage),
      },
      {
        path: Routes.messagingTemplates,
        element: withSuspense(MessagingTemplates),
      },
      {
        path: Routes.messagingCreate,
        element: withSuspense(CreateMessage),
      },
      {
        path: Routes.messagingSend,
        element: withSuspense(SendMessagePage),
      },
      {
        path: Routes.messagingDetails,
        element: withSuspense(MessagingDetails),
      },
      {
        path: Routes.notifications,
        element: <Notifications />,
      },
      {
        path: Routes.weddnProSubscription,
        element: <WeddnProSubscription />,
      },
      {
        path: Routes.support,
        element: <Support />,
      },
      {
        path: Routes.sendMessage,
        element: <SendAMessage />,
      },
      {
        path: Routes.messages,
        element: <MessagesPage />,
      },
      {
        path: Routes.faqs,
        element: <Faqs />,
      },
    ],
  },
  {
    path: Routes.notFound,
    element: <ErrorPage />,
  },
]);

export default router;
